import { useState, useEffect } from "react";
import api from "../../utils/api";
import { INP, LBL, S, THEME, SectorRow, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O2 } = THEME;

const CONTENT = {
  ne: {
    statTotalSectors:    "कुल क्षेत्रहरू",
    statTotalSectorsDelta: "प्राथमिकता क्षेत्र",
    statTotalTags:       "कुल उपक्षेत्रहरू",
    statTotalTagsDelta:  "सबै क्षेत्र",
    statActive:          "सक्रिय",
    statActiveDelta:     "लाइभ",
    statSearchResults:   "खोज नतिजा",
    statSearchDelta:     "हाल देखिएको",
    searchPlaceholder:   "🔍 क्षेत्र वा उपक्षेत्र खोज्नुहोस्…",
    countOf:             (filtered, total) => `${filtered} / ${total} क्षेत्रहरू`,
    refresh:             "↻ ताजा गर्नुहोस्",
    addBtn:              "+ क्षेत्र थप्नुहोस्",
    colIndex:            "#",
    colSector:           "क्षेत्र",
    colTags:             "उपक्षेत्रहरू",
    colCount:            "संख्या",
    colActions:          "कार्य",
    loading:             "क्षेत्रहरू लोड हुँदैछ…",
    empty:               "कुनै क्षेत्र भेटिएन।",
    addModalTitle:       "+ नयाँ क्षेत्र थप्नुहोस्",
    editModalTitle:      "✏️ क्षेत्र सम्पादन",
    lblSectorName:       "क्षेत्रको नाम",
    sectorNamePlaceholder: "क्षेत्रको नाम लेख्नुहोस्…",
    lblIcon:             "आइकन (इमोजी)",
    lblSubsectors:       "उपक्षेत्रहरू",
    lblSubsectorsRemove: "उपक्षेत्रहरू (× थिचेर हटाउनुहोस्)",
    lblAddSubsector:     "नयाँ उपक्षेत्र थप्नुहोस्",
    subsectorPlaceholder:"उपक्षेत्रको नाम…",
    noSubsectors:        "उपक्षेत्र छैन",
    addTagBtn:           "+ थप्नुहोस्",
    addingBtn:           "थपिँदैछ…",
    addSectorBtn:        "✅ क्षेत्र थप्नुहोस्",
    savingBtn:           "सुरक्षित हुँदैछ…",
    saveChangesBtn:      "💾 परिवर्तन सुरक्षित गर्नुहोस्",
    cancel:              "रद्द गर्नुहोस्",
    deleteConfirm:       "के तपाईं यो क्षेत्र मेट्न निश्चित हुनुहुन्छ?",
    errLoad:             "क्षेत्रहरू लोड गर्न असफल भयो।",
    errUpdate:           "क्षेत्र अपडेट गर्न असफल भयो।",
    errDelete:           "क्षेत्र मेट्न असफल भयो।",
    errAdd:              "क्षेत्र थप्न असफल भयो।",
  },
  en: {
    statTotalSectors:    "Total Sectors",
    statTotalSectorsDelta: "21 priorities",
    statTotalTags:       "Total Subsectors",
    statTotalTagsDelta:  "All sectors",
    statActive:          "Active",
    statActiveDelta:     "Live",
    statSearchResults:   "Search Results",
    statSearchDelta:     "Currently shown",
    searchPlaceholder:   "🔍 Search sectors or subsectors…",
    countOf:             (filtered, total) => `${filtered} / ${total} sectors`,
    refresh:             "↻ Refresh",
    addBtn:              "+ Add Sector",
    colIndex:            "#",
    colSector:           "Sector",
    colTags:             "Subsectors",
    colCount:            "Count",
    colActions:          "Actions",
    loading:             "Loading sectors…",
    empty:               "No sectors found.",
    addModalTitle:       "+ Add New Sector",
    editModalTitle:      "✏️ Edit Sector",
    lblSectorName:       "Sector Name",
    sectorNamePlaceholder: "Enter sector name…",
    lblIcon:             "Icon (Emoji)",
    lblSubsectors:       "Subsectors",
    lblSubsectorsRemove: "Subsectors (click × to remove)",
    lblAddSubsector:     "Add New Subsector",
    subsectorPlaceholder:"Subsector name…",
    noSubsectors:        "No subsectors",
    addTagBtn:           "+ Add",
    addingBtn:           "Adding…",
    addSectorBtn:        "✅ Add Sector",
    savingBtn:           "Saving…",
    saveChangesBtn:      "💾 Save Changes",
    cancel:              "Cancel",
    deleteConfirm:       "Are you sure you want to delete this sector?",
    errLoad:             "Failed to load sectors.",
    errUpdate:           "Failed to update sector.",
    errDelete:           "Failed to delete sector.",
    errAdd:              "Failed to add sector.",
  },
};

function SectorsPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const isMobile = useIsMobile();

  const [sectors, setSectors]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [search, setSearch]         = useState("");
  const [editSector, setEditSector] = useState(null);
  const [newTag, setNewTag]         = useState("");
  const [saving, setSaving]         = useState(false);

  const [addModal, setAddModal]         = useState(false);
  const [newSector, setNewSector]       = useState({ name: "", icon: "📋", tags: [] });
  const [newSectorTag, setNewSectorTag] = useState("");

  useEffect(() => { fetchSectors(); }, []);

  async function fetchSectors() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/sectors/admin/all");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setSectors(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  const filtered = sectors.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.tags || []).some(tg => tg.toLowerCase().includes(search.toLowerCase()))
  );

  const totalTags = sectors.reduce((a, s) => a + (s.tags?.length || 0), 0);

  function openEdit(s) { setEditSector({ ...s, tags: [...(s.tags || [])] }); setNewTag(""); }
  function closeModal() { setEditSector(null); setNewTag(""); }

  function removeTag(idx) {
    setEditSector(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== idx) }));
  }

  function addTag() {
    const val = newTag.trim();
    if (!val || editSector.tags.includes(val)) return;
    setEditSector(prev => ({ ...prev, tags: [...prev.tags, val] }));
    setNewTag("");
  }

  async function saveEdit() {
    if (!editSector.name.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`/sectors/${editSector._id}/full`, {
        name: editSector.name.trim(),
        icon: editSector.icon || "📋",
        description: editSector.description || "",
        tags: editSector.tags,
      });
      const updated = res.data?.data ?? res.data;
      setSectors(sectors.map(s => s._id === updated._id ? updated : s));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errUpdate);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await api.delete(`/sectors/${id}`);
      setSectors(prev => prev.filter(s => s._id !== id));
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    }
  }

  async function handleAdd() {
    if (!newSector.name.trim()) return;
    setSaving(true);
    try {
      const res     = await api.post("/sectors", newSector);
      const created = res.data?.data ?? res.data;
      setSectors(prev => [...prev, created]);
      setAddModal(false);
      setNewSector({ name: "", icon: "📋", tags: [] });
      setNewSectorTag("");
    } catch (e) {
      alert(e.response?.data?.message || t.errAdd);
    } finally {
      setSaving(false);
    }
  }

  const catBadge = { display:"inline-block", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600, background:G3, color:G, border:`1px solid ${G}`, whiteSpace:"nowrap" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Stat cards: 4 columns on desktop, 2 on mobile so they don't get squished */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap:10 }}>
        {[
          { label:t.statTotalSectors,  value:sectors.length,  icon:"🗂", delta:t.statTotalSectorsDelta },
          { label:t.statTotalTags,     value:totalTags,       icon:"🏷", delta:t.statTotalTagsDelta },
          { label:t.statActive,        value:sectors.length,  icon:"✅", delta:t.statActiveDelta },
          { label:t.statSearchResults, value:filtered.length, icon:"🔍", delta:t.statSearchDelta },
        ].map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding:"12px 16px" }}>
        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <input
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...INP, flex: "1 1 220px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 300 }}
          />
          <span style={{ fontSize:12, color:"#6b7280" }}>
            {t.countOf(filtered.length, sectors.length)}
          </span>
          <button onClick={fetchSectors}
            style={{ marginLeft: isMobile ? 0 : "auto", padding:"7px 14px", borderRadius:7, background:G3, border:`1px solid ${G}`, color:G, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            {t.refresh}
          </button>
          <button onClick={() => { setNewSector({ name:"", icon:"📋", tags:[] }); setNewSectorTag(""); setAddModal(true); }}
            style={{ padding:"7px 16px", borderRadius:7, background:G, color:"#fff", border:"none", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {t.addBtn}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 16px", color:"#ef4444", fontSize:13 }}>
          ⚠ {error}
        </div>
      )}

      {loading && (
        <div style={{ ...S.card, padding:30, textAlign:"center", color:"#9ca3af", fontSize:13 }}>{t.loading}</div>
      )}

      {!loading && (
        <div style={{ ...S.tableWrap, ...S.tableScroll }}>
          <div style={{
            display:"grid", gridTemplateColumns:"52px 1.8fr 3fr 90px 110px",
            padding:"10px 20px",
            background:"linear-gradient(180deg,#fafbfc,#f3f4f6)",
            borderBottom:"1px solid #e5e7eb",
            fontSize:10, color:"#6b7280", fontWeight:700,
            letterSpacing:"0.07em", textTransform:"uppercase",
            minWidth: isMobile ? 600 : undefined,
          }}>
            <span>{t.colIndex}</span>
            <span>{t.colSector}</span>
            <span>{t.colTags}</span>
            <span style={{ textAlign:"center" }}>{t.colCount}</span>
            <span style={{ textAlign:"right" }}>{t.colActions}</span>
          </div>

          {filtered.length === 0 && (
            <div style={{ padding:30, textAlign:"center", color:"#9ca3af", fontSize:13 }}>
              {t.empty}
            </div>
          )}

          {filtered.map((s, idx) => (
            <SectorRow
              key={s._id}
              s={s} idx={idx}
              tags={s.tags || []}
              isLast={idx === filtered.length - 1}
              onEdit={() => openEdit(s)}
              onDelete={() => handleDelete(s._id)}
            />
          ))}
        </div>
      )}

      {addModal && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setAddModal(false)}>
          <div style={{ background:"#fff", borderRadius:14, width:460, maxWidth:"95vw", boxShadow:"0 20px 60px rgba(0,0,0,0.2)", animation:"slideUp 0.22s ease" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background:`linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding:"18px 22px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ color:"#fff", fontSize:15, fontWeight:700 }}>{t.addModalTitle}</span>
              <button onClick={() => setAddModal(false)} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:16 }}>×</button>
            </div>
            <div style={{ padding:"20px 22px", display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <div style={LBL}>{t.lblSectorName}</div>
                <input style={INP} placeholder={t.sectorNamePlaceholder} value={newSector.name} onChange={e => setNewSector({ ...newSector, name:e.target.value })} />
              </div>
              <div>
                <div style={LBL}>{t.lblIcon}</div>
                <input style={{ ...INP, width:80 }} maxLength={4} value={newSector.icon} onChange={e => setNewSector({ ...newSector, icon:e.target.value })} />
              </div>
              <div>
                <div style={LBL}>{t.lblSubsectors}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, minHeight:32, padding:8, background:"#f9fafb", borderRadius:8, border:"1.5px solid #e5e7eb", marginBottom:8 }}>
                  {(newSector.tags || []).map((tg, i) => (
                    <span key={i}
                      onClick={() => setNewSector(prev => ({ ...prev, tags: (prev.tags || []).filter((_, j) => j !== i) }))}
                      style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:600, background:G3, color:G, border:`1px solid ${G}`, cursor:"pointer" }}>
                      {tg} ×
                    </span>
                  ))}
                  {(newSector.tags || []).length === 0 && <span style={{ color:"#9ca3af", fontSize:12 }}>{t.noSubsectors}</span>}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <input
                    style={{ ...INP, flex:1 }}
                    placeholder={t.subsectorPlaceholder}
                    value={newSectorTag}
                    onChange={e => setNewSectorTag(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = newSectorTag.trim();
                        if (val && !(newSector.tags || []).includes(val))
                          setNewSector(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                        setNewSectorTag("");
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const val = newSectorTag.trim();
                      if (val && !(newSector.tags || []).includes(val))
                        setNewSector(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                      setNewSectorTag("");
                    }}
                    style={{ padding:"7px 16px", borderRadius:7, background:G, color:"#fff", border:"none", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                    {t.addTagBtn}
                  </button>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:4 }}>
                <button onClick={handleAdd} disabled={saving}
                  style={{ flex:1, padding:10, borderRadius:8, background:G, color:"#fff", border:"none", fontSize:13, fontWeight:600, cursor:"pointer", opacity:saving?0.7:1 }}>
                  {saving ? t.addingBtn : t.addSectorBtn}
                </button>
                <button onClick={() => setAddModal(false)}
                  style={{ flex:1, padding:10, borderRadius:8, background:"#f3f4f6", color:"#374151", border:"1px solid #e5e7eb", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editSector && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={closeModal}>
          <div style={{ background:"#fff", borderRadius:14, width:520, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)", animation:"slideUp 0.22s ease" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background:`linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding:"18px 22px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ color:"#fff", fontSize:15, fontWeight:700 }}>{t.editModalTitle}</span>
              <button onClick={closeModal} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:16 }}>×</button>
            </div>
            <div style={{ padding:"20px 22px", display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <div style={LBL}>{t.lblSectorName}</div>
                <input style={INP} value={editSector.name} onChange={e => setEditSector({...editSector, name:e.target.value})} />
              </div>
              <div>
                <div style={LBL}>{t.lblIcon}</div>
                <input style={{...INP, width:80}} maxLength={4} value={editSector.icon || ""} onChange={e => setEditSector({...editSector, icon:e.target.value})} />
              </div>
              <div>
                <div style={LBL}>{t.lblSubsectorsRemove}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, minHeight:32, padding:8, background:"#f9fafb", borderRadius:8, border:"1.5px solid #e5e7eb" }}>
                  {editSector.tags.map((tg,i) => (
                    <span key={i}
                      onClick={() => removeTag(i)}
                      style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:600, background:G3, color:G, border:`1px solid ${G}`, cursor:"pointer" }}>
                      {tg} ×
                    </span>
                  ))}
                  {editSector.tags.length === 0 && <span style={{ color:"#9ca3af", fontSize:12 }}>{t.noSubsectors}</span>}
                </div>
              </div>
              <div>
                <div style={LBL}>{t.lblAddSubsector}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input
                    style={{ ...INP, flex:1 }}
                    placeholder={t.subsectorPlaceholder}
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    onKeyDown={e => { if(e.key==="Enter"){ e.preventDefault(); addTag(); } }}
                  />
                  <button onClick={addTag}
                    style={{ padding:"7px 16px", borderRadius:7, background:G, color:"#fff", border:"none", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                    {t.addTagBtn}
                  </button>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:4 }}>
                <button onClick={saveEdit} disabled={saving}
                  style={{ flex:1, padding:10, borderRadius:8, background:G, color:"#fff", border:"none", fontSize:13, fontWeight:600, cursor:"pointer", opacity:saving?0.7:1 }}>
                  {saving ? t.savingBtn : t.saveChangesBtn}
                </button>
                <button onClick={closeModal}
                  style={{ flex:1, padding:10, borderRadius:8, background:"#f3f4f6", color:"#374151", border:"1px solid #e5e7eb", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

export default SectorsPage;
